import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { motion, AnimatePresence } from 'framer-motion';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
import { FractionVisual, FractionVisualType } from '../../../visuals/components/FractionVisual';
export function FractionEquivalenceMatchRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  // Normalize pairs
  let pairs: Array<{
    fraction: {
      numerator: number;
      denominator: number;
      visualType?: FractionVisualType;
    };
    equivalent: string;
  }> = [];
  if (Array.isArray(config.pairs) && config.pairs.length > 0) {
    pairs = config.pairs as any;
  }
  const leftItems = useMemo(() => pairs.map((p, i) => ({
    id: i,
    fraction: p.fraction
  })), [pairs]);
  const rightItems = useMemo(() => {
    const items = pairs.map((p, i) => ({
      id: i,
      content: p.equivalent
    }));
    try {
      return shuffleWithSeed(items, question.id || 'fallback-seed').shuffled;
    } catch {
      return items;
    }
  }, [pairs, question.id]);
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [connections, setConnections] = useState<Record<number, number>>({});
  const [justConnected, setJustConnected] = useState<number | null>(null);
  useEffect(() => {
    setSelectedLeft(null);
    setConnections({});
    setJustConnected(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(Object.keys(connections).length === leftItems.length);
  }, [connections, leftItems.length, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(Object.entries(connections).map(([l, r]) => [Number(l), r])), [connections]);
  const handleEdgeSubmit = useCallback((payload: string) => {
    requestAnimationFrame(() => onAnswer(payload));
  }, [onAnswer]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, handleEdgeSubmit, question.id);
  if (pairs.length === 0) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-11504">
        <p className="text-amber-700 font-bold" data-id="element-11505">
          ⚠️ Geen koppelparen beschikbaar
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-11506">
          Deze vraag heeft geen geldige koppeldata.
        </p>
      </div>;
  }
  const handleLeftClick = (id: number) => {
    hapticManager.hapticLight();
    if (connections[id] !== undefined) {
      const next = {
        ...connections
      };
      delete next[id];
      setConnections(next);
    }
    setSelectedLeft(id);
  };
  const handleRightClick = (id: number) => {
    if (selectedLeft === null) {
      hapticManager.hapticError();
      return;
    }
    hapticManager.hapticMedium();
    const existingLeft = Object.keys(connections).find(k => connections[Number(k)] === id);
    const next = {
      ...connections
    };
    if (existingLeft) delete next[Number(existingLeft)];
    next[selectedLeft] = id;
    setConnections(next);
    setJustConnected(selectedLeft);
    setTimeout(() => setJustConnected(null), 400);
    setSelectedLeft(null);
  };
  const handleLeftKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`left-item-${(index + 1) % leftItems.length}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`left-item-${(index - 1 + leftItems.length) % leftItems.length}`)?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      document.getElementById('right-item-0')?.focus();
    }
  };
  const handleRightKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`right-item-${(index + 1) % rightItems.length}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`right-item-${(index - 1 + rightItems.length) % rightItems.length}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      document.getElementById('left-item-0')?.focus();
    }
  };
  const getPairNumber = (leftId: number): number | null => {
    const sorted = Object.keys(connections).map(Number).sort((a, b) => a - b);
    const idx = sorted.indexOf(leftId);
    return idx !== -1 ? idx + 1 : null;
  };
  const getRightPairInfo = (rightId: number) => {
    const leftId = Object.keys(connections).find(k => connections[Number(k)] === rightId);
    if (leftId === undefined) return null;
    const pairNum = getPairNumber(Number(leftId));
    return pairNum !== null ? {
      leftId: Number(leftId),
      pairNum
    } : null;
  };
  const matchedCount = Object.keys(connections).length;
  return <div className="w-full max-w-2xl mx-auto" data-id="element-11507">
      <div className="flex items-center justify-between mb-5 px-1" data-id="element-11508">
        <p className="text-slate-500 text-sm font-medium bg-slate-100 px-3 py-1 rounded-full" data-id="element-11509">
          {selectedLeft === null ? '1. Kies een breuk links' : '2. Kies de juiste waarde rechts'}
        </p>
        <div className="flex items-center gap-2" data-id="element-11510">
          <div className="flex gap-1.5" data-id="element-11511">
            {leftItems.map(item => <motion.div key={item.id} initial={false} animate={{
            scale: connections[item.id] !== undefined ? 1 : 0.75
          }} className={`w-2 h-2 rounded-full transition-colors duration-300 ${connections[item.id] !== undefined ? '' : 'bg-slate-200'}`} style={connections[item.id] !== undefined ? {
            backgroundColor: hexToRgba(color, 0.6)
          } : undefined} data-id="element-11512" />)}
          </div>
          <span className="text-xs font-semibold text-slate-400 tabular-nums" data-id="element-11513">
            {matchedCount}/{leftItems.length}
          </span>
        </div>
      </div>

      <div className="flex gap-2 sm:gap-5" data-id="element-11514">
        {/* Left Column */}
        <div className="flex-1 space-y-2.5 min-w-0" role="listbox" aria-label="Linker items" data-id="element-11515">
          {leftItems.map((item, index) => {
          const isConnected = connections[item.id] !== undefined;
          const isSelected = selectedLeft === item.id;
          const pairNum = getPairNumber(item.id);
          const wasJust = justConnected === item.id;
          return <motion.button key={item.id} id={`left-item-${index}`} role="option" aria-selected={isSelected || isConnected} tabIndex={0} onClick={() => handleLeftClick(item.id)} onKeyDown={e => handleLeftKeyDown(e, index)} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0,
            scale: wasJust ? [1, 1.04, 1] : 1
          }} transition={{
            opacity: {
              delay: index * 0.05,
              duration: 0.3
            },
            x: {
              delay: index * 0.05,
              duration: 0.3
            },
            scale: {
              duration: 0.3
            }
          }} whileTap={{
            scale: 0.97
          }} className={`w-full px-2 py-2.5 sm:px-4 sm:py-3 rounded-xl border-2 text-left transition-colors relative focus:outline-none min-h-[48px] sm:min-h-[56px] flex items-center justify-center ${isSelected ? 'z-10' : isConnected ? '' : 'border-slate-200 bg-white hover:border-slate-300 active:bg-slate-50'}`} style={isSelected ? {
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.05),
            boxShadow: `0 4px 6px -1px ${hexToRgba(color, 0.1)}, 0 2px 4px -1px ${hexToRgba(color, 0.06)}`
          } : isConnected ? {
            borderColor: hexToRgba(color, 0.2),
            backgroundColor: hexToRgba(color, 0.08)
          } : undefined} data-id="element-11516">
                {isConnected && <motion.div initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full" style={{
              backgroundColor: hexToRgba(color, 0.6)
            }} data-id="element-11517" />}
                <div className="flex-1 flex justify-center py-1 sm:py-2 pointer-events-none overflow-hidden transform scale-90 sm:scale-100" data-id="element-11518">
                  <FractionVisual numerator={item.fraction.numerator} denominator={item.fraction.denominator} type={item.fraction.visualType || 'pizza'} mode="stimulus" data-id="element-11519" />
                </div>
                <AnimatePresence data-id="element-11520">
                  {pairNum && <motion.span initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className="absolute right-1 sm:right-3 flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shadow-sm" style={{
                backgroundColor: color
              }} data-id="element-11521">
                      {pairNum}
                    </motion.span>}
                </AnimatePresence>
                {isSelected && <motion.div className="absolute inset-0 rounded-xl border-2 pointer-events-none" style={{
              borderColor: color
            }} initial={false} animate={{
              boxShadow: [`0 0 0 0px ${hexToRgba(color, 0)}`, `0 0 0 4px ${hexToRgba(color, 0.2)}`, `0 0 0 0px ${hexToRgba(color, 0)}`]
            }} transition={{
              duration: 1.5,
              repeat: Infinity
            }} data-id="element-11522" />}
              </motion.button>;
        })}
        </div>

        {/* Right Column */}
        <div className="flex-1 space-y-2.5 min-w-0" role="listbox" aria-label="Rechter items" data-id="element-11523">
          {rightItems.map((item, index) => {
          const pairInfo = getRightPairInfo(item.id);
          const isConnected = pairInfo !== null;
          const isTargetable = selectedLeft !== null && !isConnected;
          const wasJust = justConnected !== null && pairInfo?.leftId === justConnected;
          return <motion.button key={item.id} id={`right-item-${index}`} role="option" aria-selected={isConnected} tabIndex={0} onClick={() => handleRightClick(item.id)} onKeyDown={e => handleRightKeyDown(e, index)} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0,
            scale: wasJust ? [1, 1.04, 1] : 1
          }} transition={{
            opacity: {
              delay: index * 0.05,
              duration: 0.3
            },
            x: {
              delay: index * 0.05,
              duration: 0.3
            },
            scale: {
              duration: 0.3
            }
          }} whileTap={{
            scale: 0.97
          }} className={`w-full px-2 py-2.5 sm:px-4 sm:py-3 rounded-xl border-2 text-left transition-colors relative focus:outline-none min-h-[48px] sm:min-h-[56px] flex items-center ${isConnected ? '' : isTargetable ? 'border-slate-300 bg-slate-50 cursor-pointer shadow-inner' : 'border-slate-200 bg-white cursor-default opacity-80'}`} style={isConnected ? {
            borderColor: hexToRgba(color, 0.2),
            backgroundColor: hexToRgba(color, 0.08)
          } : isTargetable ? {
            '--tw-ring-color': hexToRgba(color, 0.3),
            '--tw-ring-offset-width': '0px',
            '--tw-ring-offset-color': '#fff',
            '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
            '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)'
          } as TailwindCSSProperties : undefined} onMouseEnter={e => {
            if (isTargetable) {
              e.currentTarget.style.borderColor = hexToRgba(color, 0.4);
              e.currentTarget.style.boxShadow = 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000';
            }
          }} onMouseLeave={e => {
            if (isTargetable) {
              e.currentTarget.style.borderColor = '';
              e.currentTarget.style.boxShadow = '';
            }
          }} data-id="element-11524">
                <AnimatePresence data-id="element-11525">
                  {pairInfo && <motion.span initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className="mr-2 sm:mr-3 flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shadow-sm" style={{
                backgroundColor: color
              }} data-id="element-11526">
                      {pairInfo.pairNum}
                    </motion.span>}
                </AnimatePresence>
                <div className={`text-base sm:text-xl font-bold flex-1 flex justify-center items-center min-w-0 px-1 ${isConnected ? '' : 'text-slate-700'}`} style={isConnected ? {
              color: color
            } : undefined} data-id="element-11527">
                  <span className="whitespace-nowrap" data-id="element-11528">
                    <LatexRenderer content={item.content} data-id="element-11529" />
                  </span>
                </div>
                {isConnected && <motion.div initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} className="absolute right-0 top-2 bottom-2 w-1 rounded-l-full" style={{
              backgroundColor: hexToRgba(color, 0.6)
            }} data-id="element-11530" />}
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}